import { AutoAssignedId } from './../model/AutoAssignedId';
import { collection, deleteDoc, doc, DocumentData, getDoc, getDocFromCache, getDocs, getDocsFromCache, limit, onSnapshot, orderBy, query, QuerySnapshot, setDoc, Unsubscribe, where } from "firebase/firestore"
import { db } from "../firebase"
import log from "./logger";

export async function findFromServer(table: string, pk: string[]): Promise<DocumentData> {
  const docRef = doc(db, table, ...pk);
  log(`find from firestore. table: ${table}, primary key: ${pk}`);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) throw new Error(`document is not found. table: ${table}, primary key: ${pk}`)
  return docSnap.data();
}

export async function findFromCache(table: string, pk: string[]): Promise<DocumentData> {
  const docRef = doc(db, table, ...pk);
  log(`find from firestore. table: ${table}, primary key: ${pk}`);
  let docSnap;
  try {
    docSnap = await getDocFromCache(docRef);
  } catch (e) {
    docSnap = await getDoc(docRef);
  }
  if (!docSnap.exists()) throw new Error(`document is not found. table: ${table}, primary key: ${pk}`)
  return docSnap.data();
}

export async function listFromServer(table: string, filterOps?: FilterOption[], sortOps?: SortOption[], _limit?: number): Promise<DocumentData[]> {
  const colRef = collection(db, table);
  const filters = filterOps?.map(it => where(it.key, it.condition, it.value)) ?? []
  const sorts = sortOps?.map(it => orderBy(it.key, it.condition)) ?? []
  let colSnap: QuerySnapshot<DocumentData>;
  if (filters || sorts) {
    log(`list from firestore. table: ${table}, filter option: ${filterOps}, sort option: ${sortOps}, limit: ${_limit}`);
    const q = _limit ? query(colRef, ...filters, ...sorts, limit(_limit)) : query(colRef, ...filters, ...sorts)
    colSnap = await getDocs(q);
  } else {
    log(`list from firestore. table: ${table}`);
    colSnap = await getDocs(colRef);
  }
  return colSnap.docs.map(it => it.data());
}

export async function listFromCache(table: string, filterOps?: FilterOption[], sortOps?: SortOption[], _limit?: number): Promise<DocumentData[]> {
  const colRef = collection(db, table);
  const filters = filterOps?.map(it => where(it.key, it.condition, it.value)) ?? []
  const sorts = sortOps?.map(it => orderBy(it.key, it.condition)) ?? []
  let colSnap: QuerySnapshot<DocumentData>;
  if (filters || sorts) {
    log(`list from firestore. table: ${table}, filter option: ${filterOps}, sort option: ${sortOps}, limit: ${_limit}`);
    const q = _limit ? query(colRef, ...filters, ...sorts, limit(_limit)) : query(colRef, ...filters, ...sorts)
    colSnap = await getDocsFromCache(q);
    if (colSnap.empty) colSnap = await getDocs(q)
  } else {
    log(`list from firestore. table: ${table}`);
    colSnap = await getDocsFromCache(colRef);
    if (colSnap.empty) colSnap = await getDocs(colRef)
  }
  return colSnap.docs.map(it => it.data());
}

export function watch(
  table: string,
  pk: string[],
  cb: (data: DocumentData) => void
): Unsubscribe {
  const docRef = doc(db, table, ...pk);
  return onSnapshot(docRef, snap => {
    log(`watched from firestore. table: ${table}, primary key: ${pk}`)
    cb(snap.data()!)
  })
}

export function watchList(
  table: string,
  cb: (datas: DocumentData[]) => void,
  filterOps?: FilterOption[],
  sortOps?: SortOption[],
  _limit?: number,
  changeTypes?: ('added' | 'modified' | 'removed')[]
): Unsubscribe {
  const colRef = collection(db, table);
  const filters = filterOps?.map(it => where(it.key, it.condition, it.value)) ?? []
  const sorts = sortOps?.map(it => orderBy(it.key, it.condition)) ?? []
  if (filters || sorts) {
    const q = _limit ? query(colRef, ...filters, ...sorts, limit(_limit)) : query(colRef, ...filters, ...sorts)
    return onSnapshot(q, snap => {
      log(`watched from firestore. table: ${table}, filter option: ${filterOps}, sort option: ${sortOps}`);
      if (!changeTypes) {
        cb(snap.docs.map(it => it.data()))
      } else {
        snap.docChanges().forEach(change => {
          if (changeTypes.some(it => it === change.type)) {
            cb(snap.docs.map(it => it.data()))
          }
        })
      }
    })
  } else {
    return onSnapshot(colRef, snap => {
      log(`watched from firestore. table: ${table}`)
      if (!changeTypes) {
        cb(snap.docs.map(it => it.data()))
      } else {
        snap.docChanges().forEach(change => {
          if (changeTypes.some(it => it === change.type)) {
            cb(snap.docs.map(it => it.data()))
          }
        })
      }
    })
  }
}

export function saveWithAssignId(table: string, entity: AutoAssignedId): string {
  const colRef = collection(db, table)
  const docRef = doc(colRef)
  entity.id = docRef.id  
  log(`save for firestore. table: ${table}, entity: ${entity}`)
  setDoc(docRef, entity)
  return docRef.id
}

export async function save(table: string, entity: any, pk?: string[]) {
  if (pk) {
    if (pk.length % 2 !== 0) {
      const docRef = doc(db, table, ...pk)
      log(`save for firestore. table: ${table}, pk: ${pk}, entity: ${entity}`)
      await setDoc(docRef, entity)
    } else {
      const _pk = pk.slice()
      const last = _pk.pop()
      const colRef = collection(doc(db, table, ..._pk), last!)
      log(`save for firestore. table: ${table}, pk: ${pk}, entity: ${entity}`)
      await setDoc(doc(colRef), entity)
    }
  } else {
    const docRef =  doc(db, table)
    log(`save for firestore. table: ${table}, entity: ${entity}`)
    await setDoc(docRef, entity)
  }
}

export async function update(table: string, pk: string[], entity: any) {
  const docRef = doc(db, table, ...pk);
  log(`update for firestore. table: ${table}, pk: ${pk}, entity: ${entity}`)
  setDoc(docRef, entity)
}

export function remove(table: string, pk: string[]) {
  const docRef = doc(db, table, ...pk);
  log(`remove for firestore. table: ${table}, pk: ${pk}`)
  deleteDoc(docRef)
}

export type FilterOption = {
  key: string,
  condition: '==' | '!=' | '>' | '>=' | '<' | '<=' | 'array-contains',
  value: any
}
export type SortOption = {
  key: string,
  condition: 'asc' | 'desc'
}
